const SERVER_ERROR_PREFIX = 'Cloud Run call failed: ';
const DATABASE_POLICY_VERSION = 'user-db-purge-v1';
const AUTH_ONLY_POLICY_VERSION = 'auth-only-compat-v1';
const MAX_PREVIEW_TOKEN_CHARS = 2_000_000;

const RECEIPT_STATUSES = new Set([
    'receipt_present',
    'receipt_absent',
    'receipt_ambiguous',
]);

const ERROR_COPY = {
    ADMIN_REQUIRED: 'Administrator access is required for this action.',
    OFFBOARDING_DISABLED: 'Team-member permanent deletion is currently disabled.',
    OFFBOARDING_EXECUTION_DISABLED: 'Team-member permanent deletion is not enabled.',
    CLIENT_EXECUTION_FIELDS_REJECTED: 'Deletion preview does not accept request controls.',
    DELETE_REQUEST_INVALID: 'A fresh preview and exact confirmation are required.',
    REQUEST_BODY_INVALID: 'A fresh preview and exact confirmation are required.',
    CONFIRMATION_INVALID: 'Deletion confirmation is invalid. Request a fresh preview.',
    CONFIRMATION_MISMATCH: 'Confirmation no longer matches the selected target.',
    FIREBASE_LOOKUP_FAILED: 'The existing authentication account could not be checked.',
    FIREBASE_DELETE_FAILED: 'The existing authentication deletion did not complete.',
    FIREBASE_DELETE_AFTER_DATABASE_PURGE_FAILED: 'Application data was deleted, but authentication cleanup did not complete. Request a fresh preview to finish it.',
    FIREBASE_DELETE_OUTCOME_UNKNOWN_AFTER_DATABASE_PURGE: 'Application data was deleted, but the authentication deletion outcome could not be confirmed. Do not retry automatically.',
    IDENTITY_EMAIL_MISMATCH: 'The database and Firebase emails differ. Review the identity before permanent deletion.',
    TARGET_UID_MALFORMED: 'The selected team member identifier is invalid.',
    TARGET_ALREADY_PURGED_OR_UNKNOWN: 'The selected team member was not found.',
    TARGET_AMBIGUOUS: 'The selected team member could not be resolved safely.',
    PREVIEW_TOKEN_CONFIGURATION_MISSING: 'Deletion preview is unavailable because server configuration is incomplete.',
    PREVIEW_TOKEN_CONFIGURATION_INVALID: 'Deletion preview is unavailable because server configuration is invalid.',
    PREVIEW_TOKEN_INVALID: 'The deletion preview is invalid. Request a fresh preview.',
    PREVIEW_TOKEN_TOO_LARGE: 'The deletion preview is invalid. Request a fresh preview.',
    PREVIEW_DIGEST_INVALID: 'The deletion preview is invalid. Request a fresh preview.',
    PREVIEW_TOKEN_CLAIM_DRIFT: 'The deletion preview no longer matches this request.',
    PREVIEW_ACTOR_MISMATCH: 'The deletion preview belongs to a different administrator.',
    PREVIEW_EXPIRED: 'The deletion preview expired. Request a fresh preview.',
    PREVIEW_ENVIRONMENT_DRIFT: 'The deletion environment changed. Request a fresh preview.',
    PREVIEW_POLICY_DRIFT: 'The deletion policy changed. Request a fresh preview.',
    PREVIEW_TARGET_DRIFT: 'The selected target changed. Request a fresh preview.',
    PREVIEW_COUNT_DRIFT: 'Database counts changed. Request a fresh preview.',
    PREVIEW_PLAN_DRIFT: 'The database plan changed. Request a fresh preview.',
    PREVIEW_BLOCKED: 'Database safety checks now block this deletion.',
    PREVIEW_ROLLBACK_FAILED: 'The read-only deletion preview could not close safely.',
    POSTGRES_TARGET_APPEARED: 'A database target now exists. Request a fresh database preview.',
    DIRECT_ORPHANED_DATABASE_DATA_DETECTED: 'Resolvable historical database rows require manual remediation.',
    CONNECTION_NOT_IDLE: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    TRANSACTION_SETUP_FAILED: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    COUNT_QUERY_INVALID_RESULT: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    CROSS_OWNER_PREFLIGHT_INVALID: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    ORPHAN_PREFLIGHT_INVALID: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    QUERY_PLAN_UNAVAILABLE: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    PRECOMMIT_FAILURE: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    DATABASE_OFFBOARDING_FAILED: 'Team-member deletion could not continue safely.',
    DATABASE_PURGE_FAILED: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    MUTATION_COUNT_DRIFT: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    TARGET_REMNANT_DETECTED: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    ISOLATION_INVARIANT_FAILED: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    AUDIT_RECEIPT_MISSING: 'Database cleanup was rolled back safely. Request a fresh preview before retrying.',
    COMMIT_OUTCOME_UNKNOWN: 'Database outcome requires receipt reconciliation; do not retry.',
    ROLLBACK_FAILED_UNSAFE: 'Database outcome requires receipt reconciliation; do not retry.',
};

const RETRYABLE_CODES = new Set([
    'CONNECTION_NOT_IDLE',
    'TRANSACTION_SETUP_FAILED',
    'COUNT_QUERY_INVALID_RESULT',
    'CROSS_OWNER_PREFLIGHT_INVALID',
    'ORPHAN_PREFLIGHT_INVALID',
    'QUERY_PLAN_UNAVAILABLE',
    'PRECOMMIT_FAILURE',
    'DATABASE_PURGE_FAILED',
    'MUTATION_COUNT_DRIFT',
    'TARGET_REMNANT_DETECTED',
    'ISOLATION_INVARIANT_FAILED',
    'AUDIT_RECEIPT_MISSING',
]);

const FRESH_PREVIEW_CODES = new Set([
    'DELETE_REQUEST_INVALID',
    'REQUEST_BODY_INVALID',
    'CONFIRMATION_INVALID',
    'CONFIRMATION_MISMATCH',
    'PREVIEW_TOKEN_INVALID',
    'PREVIEW_TOKEN_TOO_LARGE',
    'PREVIEW_DIGEST_INVALID',
    'PREVIEW_TOKEN_CLAIM_DRIFT',
    'PREVIEW_ACTOR_MISMATCH',
    'PREVIEW_EXPIRED',
    'PREVIEW_ENVIRONMENT_DRIFT',
    'PREVIEW_POLICY_DRIFT',
    'PREVIEW_TARGET_DRIFT',
    'PREVIEW_COUNT_DRIFT',
    'PREVIEW_PLAN_DRIFT',
    'PREVIEW_BLOCKED',
    'POSTGRES_TARGET_APPEARED',
    'DIRECT_ORPHANED_DATABASE_DATA_DETECTED',
    'FIREBASE_DELETE_AFTER_DATABASE_PURGE_FAILED',
    ...RETRYABLE_CODES,
]);

function isRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isNonEmptyString(value) {
    return typeof value === 'string' && value.length > 0;
}

function isCount(value) {
    return Number.isSafeInteger(value) && value >= 0;
}

function normalizeStringArray(value) {
    if (!Array.isArray(value) || value.some((item) => !isNonEmptyString(item))) {
        throw new OffboardingContractError('INVALID_PREVIEW_RESPONSE');
    }
    return [...value];
}

function normalizePreviewCategory(value) {
    if (!isRecord(value)
        || !isNonEmptyString(value.key)
        || !isNonEmptyString(value.label)
        || !['DELETE', 'ANONYMIZE'].includes(value.policy)
        || !isCount(value.row_count)) {
        throw new OffboardingContractError('INVALID_PREVIEW_RESPONSE');
    }
    return {
        key: value.key,
        label: value.label,
        policy: value.policy,
        row_count: value.row_count,
    };
}

function normalizeResultCategory(value) {
    if (!isRecord(value)
        || !isNonEmptyString(value.key)
        || !isNonEmptyString(value.label)
        || !isCount(value.row_count)) {
        throw new OffboardingContractError('INVALID_DELETE_RESPONSE');
    }
    return {
        key: value.key,
        label: value.label,
        row_count: value.row_count,
    };
}

function sumCounts(categories) {
    return categories.reduce((total, category) => total + category.row_count, 0);
}

function hasUniqueKeys(categories) {
    return new Set(categories.map((category) => category.key)).size === categories.length;
}

function categoriesMatchPreview(resultCategories, previewCategories) {
    if (resultCategories.length !== previewCategories.length
        || !hasUniqueKeys(resultCategories)) {
        return false;
    }
    const previewByKey = new Map(previewCategories.map((category) => [category.key, category]));
    return resultCategories.every((category) => {
        const expected = previewByKey.get(category.key);
        return expected
            && expected.label === category.label
            && expected.row_count === category.row_count;
    });
}

export class OffboardingContractError extends Error {
    constructor(code) {
        super(code === 'PREVIEW_EXPIRED'
            ? 'The deletion preview expired.'
            : 'The server returned an invalid offboarding response.');
        this.name = 'OffboardingContractError';
        this.code = code;
    }
}

/** Validate and copy only the frozen preview allowlist before React state sees it. */
export function validateTeamMemberDeletionPreview(value, expectedFirebaseUid, now = Date.now()) {
    if (!isRecord(value)
        || !['database', 'auth_only'].includes(value.preview_kind)
        || !/^[a-f0-9]{64}$/i.test(value.preview_digest || '')
        || !isNonEmptyString(value.preview_token)
        || value.preview_token.length > MAX_PREVIEW_TOKEN_CHARS
        || !isNonEmptyString(value.policy_version)
        || !isNonEmptyString(value.generated_at)
        || !isNonEmptyString(value.expires_at)
        || !isRecord(value.target)
        || value.target.firebase_uid !== expectedFirebaseUid
        || !(typeof value.target.name === 'string' || value.target.name === null)
        || !(isNonEmptyString(value.target.email) || value.target.email === null)
        || !Array.isArray(value.categories)
        || !isCount(value.total_delete_rows)
        || !isCount(value.total_anonymize_rows)
        || typeof value.can_execute !== 'boolean'
        || typeof value.required_confirmation !== 'string'
        || value.required_confirmation !== (value.target.email || '')) {
        throw new OffboardingContractError('INVALID_PREVIEW_RESPONSE');
    }

    const generatedAt = Date.parse(value.generated_at);
    const expiresAt = Date.parse(value.expires_at);
    if (!Number.isFinite(generatedAt) || !Number.isFinite(expiresAt) || expiresAt <= generatedAt) {
        throw new OffboardingContractError('INVALID_PREVIEW_RESPONSE');
    }

    const categories = value.categories.map(normalizePreviewCategory);
    const retainedDisclosures = normalizeStringArray(value.retained_disclosures);
    const sharedDisclosures = normalizeStringArray(value.shared_disclosures);
    const warnings = normalizeStringArray(value.warnings);
    const blockers = normalizeStringArray(value.blockers);
    const deleteTotal = sumCounts(categories.filter((item) => item.policy === 'DELETE'));
    const anonymizeTotal = sumCounts(categories.filter((item) => item.policy === 'ANONYMIZE'));

    if (!hasUniqueKeys(categories)
        || deleteTotal !== value.total_delete_rows
        || anonymizeTotal !== value.total_anonymize_rows
        || value.can_execute !== (blockers.length === 0)) {
        throw new OffboardingContractError('INVALID_PREVIEW_RESPONSE');
    }

    if (value.preview_kind === 'database') {
        if (value.policy_version !== DATABASE_POLICY_VERSION
            || warnings.includes('POSTGRES_CANONICAL_TARGET_ABSENT')
            || blockers.includes('DIRECT_ORPHANED_DATABASE_DATA_DETECTED')
            || (value.target.email === null) !== blockers.includes('TARGET_CONFIRMATION_UNAVAILABLE')) {
            throw new OffboardingContractError('INVALID_PREVIEW_RESPONSE');
        }
    } else if (value.policy_version !== AUTH_ONLY_POLICY_VERSION
        || !isNonEmptyString(value.target.email)
        || categories.length !== 0
        || value.total_delete_rows !== 0
        || value.total_anonymize_rows !== 0
        || !warnings.includes('POSTGRES_CANONICAL_TARGET_ABSENT')) {
        throw new OffboardingContractError('INVALID_PREVIEW_RESPONSE');
    }

    if (expiresAt <= now) {
        throw new OffboardingContractError('PREVIEW_EXPIRED');
    }

    return {
        preview_kind: value.preview_kind,
        preview_digest: value.preview_digest,
        preview_token: value.preview_token,
        policy_version: value.policy_version,
        generated_at: new Date(generatedAt).toISOString(),
        expires_at: new Date(expiresAt).toISOString(),
        target: {
            firebase_uid: value.target.firebase_uid,
            name: value.target.name,
            email: value.target.email,
        },
        categories,
        total_delete_rows: value.total_delete_rows,
        total_anonymize_rows: value.total_anonymize_rows,
        retained_disclosures: retainedDisclosures,
        shared_disclosures: sharedDisclosures,
        warnings,
        blockers,
        can_execute: value.can_execute,
        required_confirmation: value.required_confirmation,
    };
}

/** Validate and copy a fulfilled DELETE response before success or refresh. */
export function validateTeamMemberDeletionResult(value, preview) {
    if (!isRecord(value)
        || value.success !== true
        || typeof value.firebase_was_missing !== 'boolean'
        || !isRecord(value.database)
        || value.database.policy_version !== preview.policy_version
        || value.database.preview_digest !== preview.preview_digest
        || !Array.isArray(value.database.deleted_by_category)
        || !Array.isArray(value.database.anonymized_by_category)
        || !isCount(value.database.total_deleted)
        || !isCount(value.database.total_anonymized)
        || typeof value.database.committed !== 'boolean') {
        throw new OffboardingContractError('INVALID_DELETE_RESPONSE');
    }

    const deleted = value.database.deleted_by_category.map(normalizeResultCategory);
    const anonymized = value.database.anonymized_by_category.map(normalizeResultCategory);
    const previewDeleted = preview.categories.filter((category) => category.policy === 'DELETE');
    const previewAnonymized = preview.categories.filter((category) => category.policy === 'ANONYMIZE');
    if (!hasUniqueKeys([...deleted, ...anonymized])
        || sumCounts(deleted) !== value.database.total_deleted
        || sumCounts(anonymized) !== value.database.total_anonymized
        || value.database.total_deleted !== preview.total_delete_rows
        || value.database.total_anonymized !== preview.total_anonymize_rows
        || !categoriesMatchPreview(deleted, previewDeleted)
        || !categoriesMatchPreview(anonymized, previewAnonymized)) {
        throw new OffboardingContractError('INVALID_DELETE_RESPONSE');
    }

    const hasCleanupPerformed = Object.prototype.hasOwnProperty.call(
        value.database,
        'cleanup_performed',
    );
    if (preview.preview_kind === 'database') {
        if (value.database.committed !== true
            || hasCleanupPerformed
            || typeof value.database.connection_release_warning !== 'boolean') {
            throw new OffboardingContractError('INVALID_DELETE_RESPONSE');
        }
    } else if (value.database.committed !== false
        || value.database.cleanup_performed !== false
        || deleted.length !== 0
        || anonymized.length !== 0
        || value.database.total_deleted !== 0
        || value.database.total_anonymized !== 0
        || value.firebase_was_missing !== false) {
        throw new OffboardingContractError('INVALID_DELETE_RESPONSE');
    }

    return {
        success: true,
        firebase_was_missing: value.firebase_was_missing,
        database: {
            policy_version: value.database.policy_version,
            preview_digest: value.database.preview_digest,
            deleted_by_category: deleted,
            anonymized_by_category: anonymized,
            total_deleted: value.database.total_deleted,
            total_anonymized: value.database.total_anonymized,
            committed: value.database.committed,
            ...(preview.preview_kind === 'database'
                ? { connection_release_warning: value.database.connection_release_warning }
                : { cleanup_performed: false }),
        },
    };
}

function parseServerDetail(bodyText) {
    try {
        const parsed = JSON.parse(bodyText);
        return isRecord(parsed?.detail) ? parsed.detail : null;
    } catch {
        return null;
    }
}

/** Parse only allowlisted error controls and use client-owned fixed copy. */
export function parseOffboardingApiError(error) {
    const raw = typeof error?.message === 'string' ? error.message : '';
    const typed = error?.name === 'BackendHttpError' && Number.isInteger(error.status);
    if (!typed && !raw.startsWith(SERVER_ERROR_PREFIX)) {
        return {
            responseReceived: false,
            status: null,
            code: 'NO_SERVER_RESPONSE',
            message: 'No final response was received from the server.',
            retryable: false,
            requiresFreshPreview: false,
            reconciliationStatus: null,
            automaticRetryPermitted: false,
            firebaseAbsent: false,
        };
    }

    const remainder = raw.slice(SERVER_ERROR_PREFIX.length);
    const separatorIndex = remainder.indexOf(' - ');
    const parsedStatus = typed ? error.status : Number(separatorIndex >= 0 ? remainder.slice(0, separatorIndex) : Number.NaN);
    const detail = typed ? error.detail : parseServerDetail(separatorIndex >= 0 ? remainder.slice(separatorIndex + 3) : '');
    const rawCode = typeof detail?.code === 'string' ? detail.code : '';
    const code = Object.prototype.hasOwnProperty.call(ERROR_COPY, rawCode)
        ? rawCode
        : 'OFFBOARDING_REQUEST_FAILED';
    const retryable = RETRYABLE_CODES.has(code) && detail?.retryable === true;
    const requiresFreshPreview = FRESH_PREVIEW_CODES.has(code)
        && detail?.requires_fresh_preview === true;
    const rawReceiptStatus = detail?.reconciliation_status;
    const reconciliationStatus = ['COMMIT_OUTCOME_UNKNOWN', 'ROLLBACK_FAILED_UNSAFE'].includes(code)
        && RECEIPT_STATUSES.has(rawReceiptStatus)
        ? rawReceiptStatus
        : null;

    return {
        responseReceived: true,
        status: Number.isInteger(parsedStatus) && parsedStatus >= 100 && parsedStatus <= 599
            ? parsedStatus
            : null,
        code,
        message: ERROR_COPY[code] || 'Team-member deletion could not continue safely.',
        retryable,
        requiresFreshPreview,
        reconciliationStatus,
        automaticRetryPermitted: false,
        firebaseAbsent: retryable && detail?.firebase_absent === true,
    };
}
